/**
 * PRAL — Potential Renal Acid Load (4.35). Web copy.
 *
 * LINE-EQUIVALENT COPY of the mobile module services/kidney/pral.ts — the
 * header there explains the formula and the eligibility / binding contract.
 * `__tests__/pral.test.ts` (repo root) asserts the bodies below the marker are
 * identical. Edit both together.
 */

// IMPORTS-END (everything below this line is asserted identical across copies)

/** The five inputs. Any of them may be absent on raw or legacy data. */
export interface PralInputs {
  proteinG?: number | null;
  phosphorusMg?: number | null;
  potassiumMg?: number | null;
  magnesiumMg?: number | null;
  calciumMg?: number | null;
}

export const PRAL_COEFFICIENTS = {
  proteinG: 0.49,
  phosphorusMg: 0.037,
  potassiumMg: -0.021,
  magnesiumMg: -0.026,
  calciumMg: -0.013,
} as const;

export const PRAL_KEYS = ['proteinG', 'phosphorusMg', 'potassiumMg', 'magnesiumMg', 'calciumMg'] as const;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * ELIGIBILITY. Call this on the raw source object only (an analysis food as it
 * arrived, the fields a user actually typed, a verified recipe) — never on data
 * that already went through a `|| 0` adapter. 4.39: finite AND non-negative (a
 * negative amount is nonsense, never certified); existing sigs are unaffected.
 */
export function hasAllPralInputs(raw: PralInputs | null | undefined): boolean {
  if (!raw) return false;
  return PRAL_KEYS.every((k) => isFiniteNumber(raw[k]) && (raw[k] as number) >= 0);
}

/** The exact five stored numbers, in canonical order, or null when any is not finite. */
function tupleOf(values: PralInputs | null | undefined): number[] | null {
  if (!values) return null;
  const out: number[] = [];
  for (const k of PRAL_KEYS) {
    const v = values[k];
    if (!isFiniteNumber(v)) return null;
    out.push(v === 0 ? 0 : v); // −0 and 0 are the same stored value
  }
  return out;
}

/**
 * BINDING. Serialise the five numbers exactly as they are stored. Returns
 * undefined when any is missing, so a caller can spread it conditionally.
 * The caller is responsible for eligibility: only sign values whose RAW source
 * passed `hasAllPralInputs` (or came through `transferPralSig`).
 */
export function pralSigOf(stored: PralInputs | null | undefined): string | undefined {
  const tuple = tupleOf(stored);
  return tuple ? JSON.stringify(tuple) : undefined;
}

/** Known = a sig exists and matches the current stored values exactly. */
export function isPralKnown(values: PralInputs | null | undefined, sig: string | null | undefined): boolean {
  if (typeof sig !== 'string' || sig.length === 0) return false;
  return pralSigOf(values) === sig;
}

/**
 * Re-sign rebuilt numbers (scaled, divided by servings, merged, edited) — only
 * when the source certificate was valid for the source values.
 */
export function transferPralSig(
  sourceValues: PralInputs | null | undefined,
  sourceSig: string | null | undefined,
  resultValues: PralInputs | null | undefined,
): string | undefined {
  if (!isPralKnown(sourceValues, sourceSig)) return undefined;
  return pralSigOf(resultValues);
}

/** The formula. Returns null unless all five inputs are finite. */
export function computePral(values: PralInputs | null | undefined): number | null {
  const tuple = tupleOf(values);
  if (!tuple) return null;
  const [proteinG, phosphorusMg, potassiumMg, magnesiumMg, calciumMg] = tuple;
  return (
    PRAL_COEFFICIENTS.proteinG * proteinG +
    PRAL_COEFFICIENTS.phosphorusMg * phosphorusMg +
    PRAL_COEFFICIENTS.potassiumMg * potassiumMg +
    PRAL_COEFFICIENTS.magnesiumMg * magnesiumMg +
    PRAL_COEFFICIENTS.calciumMg * calciumMg
  );
}

/**
 * One thing PRAL is evaluated on: a food, or an entry that has no foods.
 * `multiplier` scales per-serving values to what was eaten (servingsConsumed).
 * `eligible` is for PRE-LOG items that have no sig yet: the flag set when the
 * raw analysis arrived.
 */
export interface PralItem {
  values: PralInputs | null | undefined;
  sig?: string | null;
  eligible?: boolean;
  multiplier?: number;
}

export interface PralSum {
  /** Sum over KNOWN items only, rounded to 0.1. Null when nothing is known. */
  value: number | null;
  knownItems: number;
  totalItems: number;
  /** True when totalItems is an ENTRY count standing in for an unknown food count (a stored day without a fresh value). */
  itemsApprox?: boolean;
}

function roundTenth(n: number): number {
  const r = Math.round(n * 10) / 10;
  return r === 0 ? 0 : r;
}

function itemPral(item: PralItem): number | null {
  const known = item.eligible === true ? tupleOf(item.values) !== null : isPralKnown(item.values, item.sig);
  if (!known) return null;
  const base = computePral(item.values);
  if (base === null) return null;
  const m = isFiniteNumber(item.multiplier) && item.multiplier > 0 ? item.multiplier : 1;
  return base * m;
}

export function sumPral(items: readonly PralItem[]): PralSum {
  let total = 0;
  let knownItems = 0;
  for (const item of items) {
    const v = itemPral(item);
    if (v === null) continue;
    total += v;
    knownItems += 1;
  }
  return { value: knownItems > 0 ? roundTenth(total) : null, knownItems, totalItems: items.length };
}

export function isPralComplete(sum: PralSum | null | undefined): boolean {
  return !!sum && sum.totalItems > 0 && sum.knownItems === sum.totalItems && sum.value !== null;
}

/** Structural view of a stored diary entry — just what the adapter needs. */
export interface PralEntryLike {
  nutrients?: PralInputs | null;
  pralSig?: string | null;
  servingsConsumed?: number | null;
  foods?: readonly { nutrients?: PralInputs | null; pralSig?: string | null }[] | null;
}

/** Rounding slack when comparing Σ foods × servings with the stored totals. */
const COHERENCE_ABS: Record<(typeof PRAL_KEYS)[number], number> = {
  proteinG: 0.25,
  phosphorusMg: 2,
  potassiumMg: 2,
  magnesiumMg: 2,
  calciumMg: 2,
};
const COHERENCE_REL = 0.02;

/**
 * Do the entry's foods still describe the entry? The per-food certificates vouch for
 * the FOODS; the day's numbers come from `entry.nutrients`. A writer that edits the
 * totals but leaves `foods` untouched (the 4.34 web editor on a combined favorite, any
 * older client) would otherwise leave a stale breakdown fully certified. Compared on
 * the five PRAL inputs only, with slack for the app's rounding (foods are stored per
 * serving and rounded; totals are rounded again after × servings).
 */
export function isEntryBreakdownCoherent(entry: PralEntryLike): boolean {
  const foods = entry.foods;
  const totals = entry.nutrients;
  if (!foods || foods.length === 0) return true;
  if (!totals) return false;
  const servings = isFiniteNumber(entry.servingsConsumed) && entry.servingsConsumed > 0 ? entry.servingsConsumed : 1;
  for (const k of PRAL_KEYS) {
    let sum = 0;
    for (const f of foods) {
      const v = f.nutrients ? f.nutrients[k] : undefined;
      if (isFiniteNumber(v)) sum += v;
    }
    const expected = sum * servings;
    const stored = totals[k];
    const actual = isFiniteNumber(stored) ? stored : 0;
    // Each food contributes its own rounding error, so the slack grows with the food count.
    const slack = COHERENCE_ABS[k] * Math.max(1, foods.length) + COHERENCE_REL * Math.abs(expected);
    if (Math.abs(expected - actual) > slack) return false;
  }
  return true;
}

/**
 * Entry → items. Foods are decomposed FIRST and judged one by one (their values
 * are per serving, so each carries the entry's servingsConsumed); only an entry
 * without foods is judged by its own entry-level sig, whose values are already
 * the consumed totals. Parent metadata (isFluid, fluidType) certifies nothing.
 * If the breakdown no longer matches the entry's totals, NOTHING is known: the
 * item count is kept (so coverage reads "0 of n"), the certificates are ignored.
 */
export function pralItemsFromEntry(entry: PralEntryLike): PralItem[] {
  const foods = entry.foods;
  if (foods && foods.length > 0) {
    if (!isEntryBreakdownCoherent(entry)) return foods.map(() => ({ values: null }));
    const servings = isFiniteNumber(entry.servingsConsumed) && entry.servingsConsumed > 0 ? entry.servingsConsumed : 1;
    return foods.map((f) => ({ values: f.nutrients, sig: f.pralSig, multiplier: servings }));
  }
  return [{ values: entry.nutrients, sig: entry.pralSig }];
}

export function pralItemsFromEntries(entries: readonly PralEntryLike[]): PralItem[] {
  const out: PralItem[] = [];
  for (const e of entries) out.push(...pralItemsFromEntry(e));
  return out;
}

/** Day value as persisted on dailySummaries/{date} by W2. */
export interface StoredDayPral {
  value: number | null;
  knownItems: number;
  totalItems: number;
}

interface PralTimestampLike {
  seconds?: number;
  nanoseconds?: number;
}

export interface PralDayDocLike {
  /** Entries logged that day (the legacy W2 count) — tells a logged-but-unknown day from an empty one. */
  totalItems?: number | null;
  pral?: StoredDayPral | null;
  pralAt?: PralTimestampLike | null;
  updatedAt?: PralTimestampLike | null;
}

/**
 * The stored day value is trustworthy only when the write that stamped it was
 * the doc's last write. A client that predates `pral` (4.34 and older) moves
 * `updatedAt` and leaves `pral` + `pralAt` behind → not fresh. A pending server
 * timestamp (no seconds yet) also reads as not fresh.
 */
export function isPralFresh(doc: PralDayDocLike | null | undefined): boolean {
  if (!doc || !doc.pral) return false;
  const a = doc.pralAt;
  const b = doc.updatedAt;
  if (!a || !b) return false;
  if (typeof a.seconds !== 'number' || typeof b.seconds !== 'number') return false;
  return a.seconds === b.seconds && a.nanoseconds === b.nanoseconds;
}

/**
 * The stored day value, or null when it cannot be trusted (stale, never written, or the
 * write is still pending). A day with no fresh value reads as "—" on range surfaces until
 * a 4.35 client opens it and rewrites the summary; it is never treated as zero.
 */
export function dayPralFromDoc(doc: PralDayDocLike | null | undefined): PralSum | null {
  if (!isPralFresh(doc)) return null;
  const p = (doc as PralDayDocLike).pral as StoredDayPral;
  if (!isFiniteNumber(p.knownItems) || !isFiniteNumber(p.totalItems)) return null;
  return { value: isFiniteNumber(p.value) ? p.value : null, knownItems: p.knownItems, totalItems: p.totalItems };
}

/**
 * A stored day as an averaging input: the trusted value when there is one; a logged day
 * without one (written by 4.34, or not rewritten since) counts as LOGGED BUT UNKNOWN, so
 * "average of 3 of 7 logged days" stays honest; an empty day is skipped.
 */
export function loggedDayPral(doc: PralDayDocLike | null | undefined): PralSum | null {
  const fresh = dayPralFromDoc(doc);
  if (fresh) return fresh;
  const n = doc ? doc.totalItems : 0;
  // The summary carries the ENTRY count, not the food count — flag it so a period never
  // prints it as "n of m foods" (Codex r6).
  return isFiniteNumber(n) && n > 0 ? { value: null, knownItems: 0, totalItems: n, itemsApprox: true } : null;
}

export interface PralAverage {
  /** Mean of the complete days, rounded to 0.1. Null when no day is complete. */
  value: number | null;
  completeDays: number;
  loggedDays: number;
}

/**
 * Average over logged days whose day value is COMPLETE. Zero and negative days
 * count; incomplete or unlogged days are never treated as zero.
 */
export function averageDailyPral(days: readonly (PralSum | null | undefined)[]): PralAverage {
  let total = 0;
  let completeDays = 0;
  let loggedDays = 0;
  for (const d of days) {
    if (!d || d.totalItems === 0) continue;
    loggedDays += 1;
    if (!isPralComplete(d)) continue;
    total += d.value as number;
    completeDays += 1;
  }
  return { value: completeDays > 0 ? roundTenth(total / completeDays) : null, completeDays, loggedDays };
}

export interface PralPeriodAverage {
  /** Mean of the daily values (complete OR subtotal) over days with at least one known food. */
  value: number | null;
  /** Foods behind the value / all foods logged in the period — the honest coverage. */
  knownItems: number;
  totalItems: number;
  daysWithValue: number;
  loggedDays: number;
  /** False when some day contributed an entry count instead of a food count — the food fraction is then not shown. */
  itemsExact: boolean;
}

/**
 * Period view for Progress and the report. Requiring every food of a day to be known made
 * a whole week read "not enough data" because of one old saved food, so the period works
 * like the day row: average what IS known and state the coverage ("34 of 41 foods"). A day
 * with nothing known (or a stale stored value) still counts in the coverage, never as zero.
 */
export function averagePralWithCoverage(days: readonly (PralSum | null | undefined)[]): PralPeriodAverage {
  let total = 0;
  let daysWithValue = 0;
  let loggedDays = 0;
  let knownItems = 0;
  let totalItems = 0;
  let itemsExact = true;
  for (const d of days) {
    if (!d || d.totalItems === 0) continue;
    loggedDays += 1;
    if (d.itemsApprox) itemsExact = false;
    totalItems += d.totalItems;
    knownItems += d.knownItems;
    if (d.value !== null && d.knownItems > 0) {
      total += d.value;
      daysWithValue += 1;
    }
  }
  return { value: daysWithValue > 0 ? roundTenth(total / daysWithValue) : null, knownItems, totalItems, daysWithValue, loggedDays, itemsExact };
}

export function isPralPeriodComplete(p: PralPeriodAverage | null | undefined): boolean {
  return !!p && p.value !== null && p.loggedDays > 0 && p.knownItems === p.totalItems && p.daysWithValue === p.loggedDays;
}

/** "+12", "−4.2", "0" — explicit sign, one decimal under 10, whole above. No unit. */
export function formatPral(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return '—';
  const abs = Math.abs(value);
  const shown = abs < 10 ? Math.round(abs * 10) / 10 : Math.round(abs);
  if (shown === 0) return '0';
  return `${value < 0 ? '−' : '+'}${shown}`;
}

/** Conditions for which PRAL tracking defaults ON (an explicit user choice always wins). */
const PRAL_DEFAULT_ON_CONDITIONS: readonly string[] = [
  'ckd_stage_3',
  'ckd_stage_3a',
  'ckd_stage_3b',
  'ckd_stage_4',
  'ckd_stage_4_5',
  'ckd_stage_5',
  'ckd_stage_5_dialysis',
  'dialysis',
  'on_dialysis',
];

export function defaultShowPral(profile: { condition?: string | null } | null | undefined): boolean {
  const condition = profile?.condition;
  return typeof condition === 'string' && PRAL_DEFAULT_ON_CONDITIONS.includes(condition);
}
